import TopNav from "@/components/TopNav";
import SideNav from "@/components/SideNav";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { CommandPalette } from "@/components/CommandPalette";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/db/client";
import { AuthPromptModal } from "@/components/AuthPromptModal";
import CursorTipLayer from "@/components/shared/CursorTip";
import { useQueryClient } from "@tanstack/react-query";
import { useUserRole } from "@/hooks/useUserRole";

const AppLayout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: userRole } = useUserRole();

  useEffect(() => {
    // Where to send an unauthenticated visitor, remembering the page they were
    // aiming at so Login can return them there after they sign in.
    const redirectToLogin = () => {
      navigate("/login", { replace: true, state: { from: location.pathname + location.search } });
    };

    // Check authentication. A visitor with no session (fresh, logged out, or a
    // cookie that expired before load) belongs on the login page, not behind a
    // modal over a blurred shell they were never signed into.
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setIsAuthenticated(false);
        redirectToLogin();
      } else {
        setIsAuthenticated(true);
        setShowAuthModal(false);
      }
    };

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        setIsAuthenticated(false);
        // A session that died mid-use (SESSION_EXPIRED: 12h expiry or a 401)
        // keeps the active user on their page behind a re-login prompt. Every
        // other signed-out state — fresh visit, deliberate logout — goes to
        // /login rather than flashing a "not logged in" modal.
        if (event === "SESSION_EXPIRED") {
          setShowAuthModal(true);
        } else {
          setShowAuthModal(false);
          redirectToLogin();
        }
      } else {
        // Invalidate all user-dependent caches on login so components re-render with fresh data
        if (session && !isAuthenticated) {
          queryClient.invalidateQueries({ queryKey: ["user-role"] });
          queryClient.invalidateQueries({ queryKey: ["user-profile"] });
          queryClient.invalidateQueries({ queryKey: ["subsheets"] });
        }
        setIsAuthenticated(true);
        setShowAuthModal(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location, isAuthenticated, queryClient]);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <AuthPromptModal open={showAuthModal} />
      {/* XBG shell (design-system chrome): white top bar spanning the full
          width, structural navy sidebar on the left, content on the muted
          canvas. */}
      <div className={`flex h-screen flex-col overflow-hidden bg-background ${showAuthModal ? "blur-sm" : ""}`}>
        <TopNav />

        <div className="flex flex-1 overflow-hidden">
          <SideNav />

          <main className="flex flex-1 flex-col overflow-hidden bg-background">
            <Outlet />
          </main>
        </div>
        <CommandPalette />
      </div>
      {/* One tooltip element for the whole app; anything carrying
          `cursorTip(...)` feeds it. Outside the blurring shell so the tip is
          never blurred with the page behind an auth prompt. */}
      <CursorTipLayer />
    </>
  );
};

export default AppLayout;
